import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Button,
  Alert,
  TextField,
  Autocomplete,
  Grid,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import PropTypes from "prop-types";

export default function ExpenseBreakdownDialog({
  open,
  onClose,
  startDate,
  endDate,
  selectedLedger,
  breakdownData = [],
  loading = false,
  error = null,
}) {
  const [filteredData, setFilteredData] = useState([]);
  const [searchColumn, setSearchColumn] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
  const [columnOptions, setColumnOptions] = useState([]);
  const gridRef = useRef();

  // Format currency for Indian numbering system
  const formatIndianCurrency = (value) => {
    if (value == null || value === "") return "";
    return Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Define column definitions for AG Grid
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Date",
        field: "date",
        sortable: true,
        filter: true,
        width: 120,
      },
      {
        headerName: "Voucher ID",
        field: "voucherDisplayID",
        sortable: true,
        filter: true,
        width: 130,
      },
      {
        headerName: "Particulars",
        field: "particulars",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 200,
      },
      {
        headerName: "Amount",
        field: "amount",
        sortable: true,
        filter: true,
        width: 150,
        valueFormatter: (params) => formatIndianCurrency(params.value),
        cellStyle: (params) => ({
          fontWeight: "bold",
          textAlign: "right",
          color: params.value < 0 ? "#d32f2f" : "#2e7d32",
        }),
        type: "numericColumn",
      },
      {
        headerName: "Remarks",
        field: "remarks",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 200,
      },
    ],
    []
  );

  // Initialize column options for search
  useEffect(() => {
    const options = columnDefs
      .filter((col) => col.field)
      .map((col) => ({
        label: col.headerName,
        value: col.field,
        field: col.field,
        type: col.type || "string",
      }));
    setColumnOptions(options);

    // Auto-select the first column (Date) by default
    if (options.length > 0 && !searchColumn) {
      setSearchColumn(options[0]);
    }
  }, [columnDefs]);

  // Initialize data
  useEffect(() => {
    setFilteredData(breakdownData);
  }, [breakdownData]);

  // Filter data based on search
  useEffect(() => {
    if (!searchText.trim() || !searchColumn) {
      setFilteredData(breakdownData);
      return;
    }

    const lowerSearchText = searchText.toLowerCase();
    const filtered = breakdownData.filter((row) => {
      const fieldValue = row[searchColumn.field];
      if (fieldValue == null || fieldValue === undefined) return false;

      // Handle numeric fields differently
      if (searchColumn.type === "numericColumn" || typeof fieldValue === "number") {
        return fieldValue.toString().includes(searchText);
      }

      return fieldValue.toString().toLowerCase().includes(lowerSearchText);
    });

    setFilteredData(filtered);
  }, [breakdownData, searchColumn, searchText]);

  // Calculate footer totals for filtered rows
  const calculateFooterTotals = useCallback((data) => {
    if (!data || data.length === 0) {
      return {
        amount: 0,
        count: 0,
      };
    }

    return {
      amount: data.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0),
      count: data.length,
    };
  }, []);

  // Update footer when data or filter changes
  useEffect(() => {
    const totals = calculateFooterTotals(filteredData);

    const footerRow = {
      date: "",
      voucherDisplayID: "",
      particulars: searchText && searchColumn ? "Total (Filtered)" : "Grand Total",
      amount: totals.amount,
      remarks: `${totals.count} entries`,
    };

    setPinnedBottomRowData([footerRow]);
  }, [filteredData, searchText, searchColumn, calculateFooterTotals]);

  // Handle search input changes
  const handleSearchColumnChange = (event, newValue) => {
    setSearchColumn(newValue);
    if (!newValue) {
      setSearchText("");
    }
  };

  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchColumn(columnOptions[0] || null);
    setSearchText("");
  };

  // Handle Excel download
  const handleDownloadExcel = () => {
    if (!filteredData || filteredData.length === 0) return;

    const allData = [...filteredData, ...pinnedBottomRowData];

    // Re-map to readable column structure
    const exportData = allData.map((row) => ({
      Date: row.date || "",
      "Voucher ID": row.voucherDisplayID || "",
      Particulars: row.particulars || "",
      Amount: row.amount || 0,
      Remarks: row.remarks || "",
    }));

    // Create worksheet & workbook
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Expense Breakdown");

    // Auto column width
    const maxWidths = [];
    exportData.forEach((row) => {
      Object.values(row).forEach((val, i) => {
        const len = val ? val.toString().length : 10;
        maxWidths[i] = Math.max(maxWidths[i] || 10, len);
      });
    });
    worksheet["!cols"] = maxWidths.map((w) => ({ wch: w + 2 }));

    // Write to file
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `Expense_Breakdown_${selectedLedger}_${startDate}_to_${endDate}.xlsx`
    );
  };

  // Debug: Log when component renders
  useEffect(() => {
    if (open) {
      console.log("Dialog opened with data:", breakdownData);
    }
  }, [open, breakdownData]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#ffffff",
          borderRadius: 2,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          minHeight: "600px",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#282a2c",
          color: "#ffffff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 2,
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#ffffff" }}>
          Expense Breakdown - {selectedLedger}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#ffffff" }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 3,
          flexGrow: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: "400px",
        }}
      >
        {/* Search Controls */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2, flexShrink: 0, mt: 0.1 }}>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={columnOptions}
              getOptionLabel={(option) => option.label}
              value={searchColumn}
              onChange={handleSearchColumnChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Column"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
              disableClearable={false}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search Text"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={handleSearchTextChange}
              placeholder={
                searchColumn ? `Search in ${searchColumn.label}...` : "Select a column first"
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleClearSearch}
                disabled={!searchText}
                size="small"
                sx={{ color: "#252121" }}
              >
                Clear Search
              </Button>
              <Typography variant="caption" color="text.secondary">
                {searchText && searchColumn
                  ? `Showing ${filteredData.length} of ${breakdownData.length} rows`
                  : `Total ${breakdownData.length} rows`}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Date Range Info */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: "grey.50",
            borderRadius: 1,
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing data from <strong>{startDate}</strong> to <strong>{endDate}</strong>
          </Typography>
        </Paper>

        {/* Loading State */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
            }}
          >
            <CircularProgress sx={{ color: "primary.main" }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
            {error}
          </Alert>
        ) : filteredData.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
              textAlign: "center",
            }}
          >
            <Typography color="text.secondary">
              {searchText && searchColumn
                ? "No matching records found"
                : "No breakdown data available"}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: "300px",
              overflow: "hidden",
            }}
          >
            <div
              className="ag-theme-alpine"
              style={{
                flex: 1,
                width: "100%",
                minHeight: "300px",
              }}
            >
              <AgGridReact
                ref={gridRef}
                rowData={filteredData}
                columnDefs={columnDefs}
                pinnedBottomRowData={pinnedBottomRowData}
                defaultColDef={{
                  resizable: true,
                  filter: true,
                  sortable: true,
                }}
                getRowStyle={(params) => {
                  if (params.node.rowPinned === "bottom") {
                    return {
                      fontWeight: "bold",
                      backgroundColor: "#f5f5f5",
                    };
                  }
                  return null;
                }}
                pagination={true}
                paginationPageSize={20}
                paginationPageSizeSelector={[10, 20, 50, 100]}
                domLayout="autoHeight"
                headerHeight={48}
                rowHeight={44}
                suppressAggFuncInHeader={true}
                onGridReady={(params) => {
                  params.api.sizeColumnsToFit();
                }}
                onFirstDataRendered={(params) => {
                  params.api.sizeColumnsToFit();
                }}
              />
            </div>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Button
          onClick={handleDownloadExcel}
          variant="contained"
          disabled={filteredData.length === 0}
          sx={{
            bgcolor: "#f57c00",
            "&:hover": {
              bgcolor: "#ffffff",
              color: "#f57c00",
              border: "1px solid #f57c00",
            },
            color: "#ffffff",
            mr: 1,
          }}
        >
          Download Excel
        </Button>
        <Button
          onClick={onClose}
          sx={{
            color: "primary.main",
            "&:hover": {
              bgcolor: "rgba(255, 152, 0, 0.1)",
            },
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            bgcolor: "#f57c00",
            "&:hover": {
              bgcolor: "#ffffff",
              color: "#f57c00",
              border: "1px solid #f57c00",
            },
            color: "#ffffff",
            mr: 1,
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ExpenseBreakdownDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  selectedLedger: PropTypes.string.isRequired,
  breakdownData: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
};
